/**
 * `useT()` - translation hook for UI labels.
 *
 *   const t = useT();
 *   t('nav.method')                          // -> "METHOD"
 *   t('footer.copyright', { year: 2026 })    // -> "© 2026 DOYOURSTEP"
 *
 * Resolution order: active language → English → key (so a missing
 * translation degrades to the key string instead of crashing the UI).
 *
 * Three bundles per language, merged at module load: the marketing site
 * (`en.json`), the app surface (`app.en.json`) and the quiz (`quiz.en.json`).
 * They are split by surface only so each file stays editable by hand — keys
 * share one flat namespace and must not collide.
 */
import { useCallback, useMemo } from 'react';
import { useLang } from './LangContext';
import { Lang, pluralForm, PluralForm } from './detectLanguage';
import en from './locales/en.json';
import uk from './locales/uk.json';
import appEn from './locales/app.en.json';
import appUk from './locales/app.uk.json';
import quizEn from './locales/quiz.en.json';
import quizUk from './locales/quiz.uk.json';

type Bundle = Record<string, string>;

const bundles: Record<Lang, Bundle> = {
    en: { ...(en as Bundle), ...(appEn as Bundle), ...(quizEn as Bundle) },
    uk: { ...(uk as Bundle), ...(appUk as Bundle), ...(quizUk as Bundle) },
};

const FALLBACK: Bundle = bundles.en;

export type Vars = Record<string, string | number>;

export type TFn = (key: string, vars?: Vars) => string;

/**
 * Count-aware lookup: `tn('app.state.points', 3)` reads `app.state.points.few`
 * in Ukrainian and `app.state.points.many` in English, with `{n}` interpolated.
 */
export type TnFn = (key: string, n: number, vars?: Vars) => string;

function interpolate(template: string, vars?: Vars): string {
    if (!vars) return template;

    return template.replace(/\{(\w+)\}/g, (_, name: string) => {
        const v = vars[name];

        return v === undefined || v === null ? `{${name}}` : String(v);
    });
}

function lookup(lang: Lang, key: string): string | undefined {
    const bundle = bundles[lang] || FALLBACK;

    return bundle[key] ?? FALLBACK[key];
}

export function translate(lang: Lang, key: string, vars?: Vars): string {
    return interpolate(lookup(lang, key) ?? key, vars);
}

/**
 * Fallback chain per form, most specific first, so an English bundle only has to
 * carry `one` and `many` while Ukrainian carries all three.
 */
const FORM_ORDER: Record<PluralForm, PluralForm[]> = {
    one: ['one', 'many', 'few'],
    few: ['few', 'many', 'one'],
    many: ['many', 'few', 'one'],
};

export function translatePlural(lang: Lang, key: string, n: number, vars?: Vars): string {
    const order = FORM_ORDER[pluralForm(lang, n)];
    const raw = order.map((f) => lookup(lang, `${key}.${f}`)).find((v) => v !== undefined) ?? lookup(lang, key) ?? key;

    return interpolate(raw, { n, ...vars });
}

export function useT(): TFn {
    const { lang } = useLang();

    return useCallback((key, vars) => translate(lang, key, vars), [lang]);
}

export function useTn(): TnFn {
    const { lang } = useLang();

    return useCallback((key, n, vars) => translatePlural(lang, key, n, vars), [lang]);
}

/** Both lookups plus the active language — what most app screens need. */
export function useTt(): { t: TFn; tn: TnFn; lang: Lang } {
    const { lang } = useLang();

    return useMemo(
        () => ({
            t: (key: string, vars?: Vars) => translate(lang, key, vars),
            tn: (key: string, n: number, vars?: Vars) => translatePlural(lang, key, n, vars),
            lang,
        }),
        [lang],
    );
}
